import { Component, input, output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { labelForClaimType } from '../../utils/claim-details.util';
import { ClaimSummaryComponent } from '../claim-summary/claim-summary.component';

@Component({
  selector: 'app-preview-claim',
  standalone: true,
  imports: [CommonModule, ClaimSummaryComponent],
  templateUrl: './preview-claim.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./preview-claim.component.css']
})
export class PreviewClaimComponent {
  claim = input.required<any>();
  party = input<any>();
  request = input<any>();
  showActions = input<boolean>(true);
  isSubmitting = input<boolean>(false);

  approve = output<void>();
  reject = output<void>();

  get vehiclePlate(): string {
    return this.claim()?.vehicle?.plate
      ?? this.request()?.vehicle?.plate
      ?? 'S/N';
  }

  get claimTypeLabel(): string {
    return labelForClaimType(this.claim()?.claimType ?? this.request()?.processSlug);
  }

  get isTotalLoss(): boolean {
    return (this.claim()?.claimType ?? this.request()?.processSlug) === 'TOTAL_LOSS';
  }

  get vehicleValue(): number {
    return Number(this.claim()?.vehicle?.commercialValue ?? 0);
  }

  get laborCost(): number {
    return Number(this.claim()?.laborCost ?? 0);
  }

  get partsCost(): number {
    return Number(this.claim()?.partsCost ?? 0);
  }

  get totalCost(): number {
    if (this.isTotalLoss) {
      const hasDetails = (this.claim()?.details ?? []).length > 0;
      return hasDetails ? this.vehicleValue : 0;
    }
    return Number(this.claim()?.totalCost ?? 0);
  }

  get totalDeductible(): number {
    const claimDed = Number(this.claim()?.deductible ?? 0);
    if (claimDed > 0) return claimDed;
    return (this.claim()?.details ?? []).reduce(
      (acc: number, d: any) => acc + Number(d.deductible ?? 0),
      0
    );
  }

  get netReimbursement(): number {
    return Math.max(0, this.totalCost - this.totalDeductible);
  }

  get retentionPct(): number {
    return 15;
  }

  get isNegligence(): boolean | null {
    const details = this.claim()?.details ?? [];
    if (details.length) {
      if (details.some((d: any) => d.isNegligence === true)) return true;
      return false;
    }
    const v = this.claim()?.isNegligence;
    if (v === true || v === false) return v;
    return null;
  }

  get vehicleLabel(): string {
    const v = this.claim()?.vehicle ?? this.request()?.vehicle;
    if (!v) return this.vehiclePlate;
    const brand = v.brand?.name ?? v.brandName ?? '';
    const model = v.model?.name ?? v.modelName ?? '';
    const desc = `${brand} ${model}`.trim();
    return desc ? `${this.vehiclePlate} · ${desc}` : this.vehiclePlate;
  }

  get folio(): string {
    const uuid = this.claim()?.uuid ?? this.request()?.uuid;
    return uuid ? String(uuid).slice(0, 8).toUpperCase() : '—';
  }

  get createdAtLabel(): string {
    const raw = this.request()?.createdAt ?? this.claim()?.createdAt;
    if (!raw) return '';
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('es-CL');
  }

  get statusLabel(): string {
    return this.request()?.statusObj?.name || this.request()?.statusSlug || 'Pendiente';
  }

  get partyName(): string {
    const p = this.party();
    return `${p?.names ?? ''} ${p?.lastNames ?? ''}`.trim() || p?.names || '';
  }

  get entityLabel(): string {
    const p = this.party();
    const entity = p?.entity ?? this.request()?.entity;
    if (entity?.code && entity?.name) return `${entity.code} - ${entity.name}`;
    return entity?.name || p?.entityName || '';
  }

  get groupLabel(): string {
    const p = this.party();
    return p?.group?.name ?? p?.groupName ?? '';
  }

  get requesterLabel(): string {
    const req = this.request();
    const email = String(req?.createdByEmail ?? '').trim();
    const role = String(req?.createdByRole ?? '').trim();
    if (email && role) return `${role} · ${email}`;
    return email || role || '';
  }
}
